/* eslint-disable @typescript-eslint/member-ordering */
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ApiService } from '../../../api/api.service';
import {
  getAbstractRouteGroupActions,
  getAbstractRouteGroupFailed,
  getAbstractRouteGroupSuccess,
  getRouteGroupActions,
  getRouteGroupConfigFailed,
  getRouteGroupConfigRequested,
  getRouteGroupConfigSuccess,
  getRouteGroupFailed,
  getRouteGroupRequestedFromRouteGroupDetails,
  getRouteGroupScheduleFailed,
  getRouteGroupScheduleRequestedFromRouteGroupDetails,
  getRouteGroupScheduleSuccess,
  getRouteGroupScheduleVersionsByWeekFailed,
  getRouteGroupScheduleVersionsByWeekFromRouteGroupDetails,
  getRouteGroupScheduleVersionsByWeekSuccess,
  getRouteGroupSuccess,
  routeGroupActivitySyncFlagToggled,
  setRouteGroupActivitySyncFlagFailed,
  setRouteGroupActivitySyncFlagRequested,
  setRouteGroupActivitySyncFlagSuccess,
} from '../actions/route-group.actions';
import { catchError, filter, map, mergeMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { WpError } from '@rootTypes/entities/common';
import { Store } from '@ngrx/store';
import { SnackbarService } from '../../../shared/snackbar/snackbar.service';
import { openErrorPopup } from '@rootStore/actions';
import { GetRouteGroupRequest } from '../../../api/endpoints/get-route-group';

@Injectable()
export class RouteGroupDataEffects {
  constructor(
    private actions$: Actions,
    private api: ApiService,
    private store: Store,
    private snackbar: SnackbarService,
  ) {}

  public getRouteGroupRequested$ = createEffect(() =>
    this.actions$.pipe(
      ofType(...[...getRouteGroupActions, getRouteGroupRequestedFromRouteGroupDetails]),
      mergeMap(({ routeGroupId, dateRange, scheduleVersion, districtProgramIds, routeGroupAssignmentType }) => {
        const request = { routeGroupId } as GetRouteGroupRequest;
        if (scheduleVersion) {
          request.routeGroupScheduleId = scheduleVersion;
        } else if (dateRange) {
          request.dateRange = {
            from: dateRange.startDate,
            to: dateRange.endDate,
          };
        }
        if (districtProgramIds?.length) {
          request.districtProgramIds = districtProgramIds;
        }
        if (routeGroupAssignmentType) {
          request.routeGroupAssignmentType = routeGroupAssignmentType;
        }
        request.isAbstract = false;
        return this.api.getRouteGroup(request).pipe(
          map((response) => getRouteGroupSuccess({ routeGroup: response })),
          catchError((originalError) => {
            console.error(originalError);
            const error: WpError = {
              originalError,
              text: 'Failed to load route',
            };
            return of(getRouteGroupFailed({ routeGroupId, error }));
          }),
        );
      }),
    ),
  );

  public getRouteGroupRequestedForList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(...getAbstractRouteGroupActions),
      mergeMap(({ routeGroupId, dateRange, scheduleVersion, districtProgramIds, routeGroupAssignmentType }) => {
        const request = { routeGroupId } as GetRouteGroupRequest;
        if (scheduleVersion) {
          request.routeGroupScheduleId = scheduleVersion;
        } else if (dateRange) {
          request.dateRange = {
            from: dateRange.startDate,
            to: dateRange.endDate,
          };
        }
        if (districtProgramIds?.length) {
          request.districtProgramIds = districtProgramIds;
        }
        if (routeGroupAssignmentType) {
          request.routeGroupAssignmentType = routeGroupAssignmentType;
        }
        request.isAbstract = true;
        return this.api.getRouteGroup(request).pipe(
          map((response) => getAbstractRouteGroupSuccess({ routeGroup: response })),
          catchError((originalError) => {
            console.error(originalError);
            const error: WpError = {
              originalError,
              text: 'Failed to load route',
            };
            return of(getAbstractRouteGroupFailed({ routeGroupId, error }));
          }),
        );
      }),
    ),
  );

  public getRouteGroupConfigRequested$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getRouteGroupConfigRequested),
      mergeMap(({ districtId }) => {
        return this.api.getRouteGroupConfig({ districtId }).pipe(
          map(
            (response) => getRouteGroupConfigSuccess({ districtId, config: response }),
            catchError((originalError) => {
              const error: WpError = {
                originalError,
                text: 'Failed to get route group config',
                retryFn: () => this.store.dispatch(getRouteGroupConfigRequested({ districtId })),
              };
              return of(getRouteGroupConfigFailed({ districtId, error }));
            }),
          ),
        );
      }),
    ),
  );

  public getRouteGroupScheduleVersionsByWeekRequested$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getRouteGroupScheduleVersionsByWeekFromRouteGroupDetails),
      mergeMap(({ routeGroupId, week, routeGroupAssignmentType }) => {
        return this.api
          .getRouteGroup({
            routeGroupId,
            dateRange: { from: week.startDate, to: week.endDate },
            routeGroupAssignmentType,
            isAbstract: false,
          })
          .pipe(
            map((response) => {
              return getRouteGroupScheduleVersionsByWeekSuccess({ routeGroupId, week, routeGroup: response });
            }),
            catchError((originalError) => {
              console.error(originalError);
              const error: WpError = {
                originalError,
                text: 'Failed to load schedule versions by week for a route',
              };
              return of(getRouteGroupScheduleVersionsByWeekFailed({ routeGroupId, week, error }));
            }),
          );
      }),
    ),
  );

  public getRouteGroupScheduleRequested$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getRouteGroupScheduleRequestedFromRouteGroupDetails),
      filter(({ routeGroupScheduleId }) => !!routeGroupScheduleId),
      mergeMap(({ routeGroupId, routeGroupScheduleId, routeGroupAssignmentType }) => {
        return this.api
          .getRouteGroup({ routeGroupId, routeGroupScheduleId, routeGroupAssignmentType, isAbstract: false })
          .pipe(
            map((response) => getRouteGroupScheduleSuccess({ routeGroupSchedule: response.routeGroupSchedule })),
            catchError((originalError) => {
              console.error(originalError);
              const error: WpError = {
                originalError,
                text: 'Failed to load schedule',
              };
              return of(getRouteGroupScheduleFailed({ routeGroupScheduleId, error }));
            }),
          );
      }),
    ),
  );

  public routeGroupActivitySyncFlagToggled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(routeGroupActivitySyncFlagToggled),
      map(({ routeGroupId, enabled }) => setRouteGroupActivitySyncFlagRequested({ routeGroupId, enabled })),
    ),
  );

  public setRouteGroupActivitySyncFlagRequested$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setRouteGroupActivitySyncFlagRequested),
      mergeMap(({ routeGroupId, enabled }) => {
        return this.api.setRouteGroupActivitySyncFlag({ routeGroupId, isActivitySyncEnabled: enabled }).pipe(
          map(() => setRouteGroupActivitySyncFlagSuccess({ routeGroupId, enabled })),
          catchError((originalError) => {
            console.error(originalError);
            const error: WpError = {
              originalError,
              text: 'Failed to set activity sync',
            };
            return of(setRouteGroupActivitySyncFlagFailed({ routeGroupId, error }));
          }),
        );
      }),
    ),
  );

  public setRouteGroupActivitySyncFlagSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setRouteGroupActivitySyncFlagSuccess),
        tap(() => {
          this.snackbar.success('Successfully updated settings');
        }),
      ),
    { dispatch: false },
  );

  public setRouteGroupActivitySyncFlagFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setRouteGroupActivitySyncFlagFailed),
      map(({ error }) => {
        return openErrorPopup({ error, closePrevious: true });
      }),
    ),
  );
}
